import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { DataListCardField } from "@/components/shared/DataListCardField";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  listPlatformRequests,
  type PlatformRequestListItem,
} from "@/services/platformRequestsService";
import type { NotarialRequestStatus } from "@/services/notaryRequestsService";

const STATUS_OPTIONS: { value: NotarialRequestStatus | ""; labelKey: string }[] = [
  { value: "", labelKey: "notary.requests.status" },
  { value: "pending", labelKey: "notary.requests.statusPending" },
  { value: "in_progress", labelKey: "notary.requests.statusInProgress" },
  { value: "completed", labelKey: "notary.requests.statusCompleted" },
  { value: "missing_information", labelKey: "notary.requests.statusMissingInfo" },
  { value: "cannot_complete", labelKey: "notary.requests.statusCannotComplete" },
  {
    value: "document_ready_at_professional",
    labelKey: "notary.requests.statusDocumentReadyAtProfessional",
  },
  { value: "document_at_platform", labelKey: "notary.requests.statusDocumentAtPlatform" },
  {
    value: "awaiting_consumer_delivery_details",
    labelKey: "notary.requests.statusAwaitingConsumerDelivery",
  },
  {
    value: "awaiting_shipment_to_consumer",
    labelKey: "notary.requests.statusAwaitingShipment",
  },
  {
    value: "in_transit_to_consumer",
    labelKey: "notary.requests.statusInTransitToConsumer",
  },
];

const STATUS_VARIANTS: Record<NotarialRequestStatus, StatusBadgeVariant> = {
  // Platform/admin view — these rows are technically visible to admins
  // when inspecting raw data, even though professionals never see them.
  // Same visual as `pending`.
  pending_profile_completion: "warning",
  pending_payment_verification: "warning",
  pending: "warning",
  in_progress: "info",
  completed: "success",
  missing_information: "danger",
  cannot_complete: "softDanger",
  document_ready_at_professional: "warning",
  document_at_platform: "info",
  awaiting_consumer_delivery_details: "info",
  awaiting_shipment_to_consumer: "info",
  in_transit_to_consumer: "info",
};

function typeLabelForRow(
  row: Pick<PlatformRequestListItem, "requestType" | "sourceRequestTitle">,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const label = t(`notary.requests.requestType_${row.requestType ?? "notarial_certificate"}`, {
    defaultValue: row.requestType ?? "notarial_certificate",
  });
  return row.sourceRequestTitle ? `${label} - ${row.sourceRequestTitle}` : label;
}

function StatusPill({ status, t }: { status: NotarialRequestStatus; t: (k: string) => string }): JSX.Element {
  const opt = STATUS_OPTIONS.find((o) => o.value === status);
  const label = opt ? t(opt.labelKey) : status;
  return <StatusBadge variant={STATUS_VARIANTS[status] ?? "info"}>{label}</StatusBadge>;
}

export function PlatformRequestsPage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const showRequestTypeColumn = user?.platformRole === "root_admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl();

  const filterStatus = (searchParams.get("status") ?? "") as NotarialRequestStatus | "";

  const setFilterStatus = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set("status", value);
          else next.delete("status");
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const listParams = useMemo(
    () => ({
      limit: pagination.limit,
      offset: (pagination.page - 1) * pagination.limit,
      ...(filterStatus && { status: filterStatus }),
    }),
    [pagination.page, pagination.limit, filterStatus]
  );

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ["platform", "requests", listParams],
    queryFn: () => listPlatformRequests(listParams),
  });

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(t("platform.requests.loadError"));
    }
  }, [isError, t]);

  const items: PlatformRequestListItem[] = data?.items ?? [];

  if (isLoading) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex min-h-[40vh] items-center justify-center">
          <LoadingLogo size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("navigation.requests")}
        description={t("platform.requests.subtitle")}
      />
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Select
          value={filterStatus || "all"}
          onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t("notary.requests.status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("notary.requests.allStatuses")}</SelectItem>
            {STATUS_OPTIONS.filter((o) => o.value).map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <TableCard className="mt-4">
        {items.length === 0 ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <EmptyState
              title={t("platform.requests.emptyTitle")}
              message={t("platform.requests.emptyMessage")}
            />
          </div>
        ) : (
          <>
            <ResponsiveListSplit
              table={
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("platform.requests.applicant")}</TableHead>
                      {showRequestTypeColumn ? (
                        <TableHead>{t("platform.requests.requestTypeColumn")}</TableHead>
                      ) : null}
                      <TableHead>{t("notary.requests.status")}</TableHead>
                      <TableHead>{t("platform.requests.assignee")}</TableHead>
                      <TableHead>{t("notary.requests.createdAt")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((row) => (
                      <TableRow
                        key={row.id}
                        className="cursor-pointer transition-colors duration-150"
                        onClick={() => navigate(`/requests/${row.id}`)}
                      >
                        <TableCell className="font-medium">
                          {row.applicantName ?? row.applicantEmail ?? "—"}
                        </TableCell>
                        {showRequestTypeColumn ? (
                          <TableCell className="max-w-[220px] truncate text-muted-foreground">
                            {typeLabelForRow(row, t)}
                          </TableCell>
                        ) : null}
                        <TableCell>
                          <StatusPill status={row.status} t={t} />
                        </TableCell>
                        <TableCell>{row.acceptedByName ?? "—"}</TableCell>
                        <TableCell>{formatDisplayDate(row.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              }
              cards={
                <div className="divide-y divide-border">
                  {items.map((row) => {
                    const typeLabel = typeLabelForRow(row, t);
                    return (
                      <DataListCardRow
                        key={row.id}
                        onClick={() => navigate(`/requests/${row.id}`)}
                      >
                        <p className="font-semibold leading-snug text-foreground">
                          {row.applicantName ?? row.applicantEmail ?? "—"}
                        </p>
                        <div className="mt-3 space-y-3">
                          {showRequestTypeColumn ? (
                            <DataListCardField label={t("platform.requests.requestTypeColumn")}>
                              <span className="text-muted-foreground">{typeLabel}</span>
                            </DataListCardField>
                          ) : null}
                          <DataListCardField label={t("notary.requests.status")}>
                            <StatusPill status={row.status} t={t} />
                          </DataListCardField>
                          <DataListCardField label={t("platform.requests.assignee")}>
                            {row.acceptedByName ?? "—"}
                          </DataListCardField>
                          <DataListCardField label={t("notary.requests.createdAt")}>
                            {formatDisplayDate(row.createdAt)}
                          </DataListCardField>
                        </div>
                      </DataListCardRow>
                    );
                  })}
                </div>
              }
            />
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("notary.requests.rowsPerPage")}
              pageOfLabel={t("notary.requests.pageOf", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total,
              })}
              onRefresh={() => {
                void refetch();
              }}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
